import React, { useState, useEffect, useCallback } from "react";

/**
 * TutorialOverlay — guided 4-step cinematic tour.
 *
 * The card floats at the bottom-centre.  Each step can optionally move
 * the camera to a relevant planet via flyForView() (no panel opens).
 * The galaxy stays fully interactive behind the overlay.
 */

const STEPS = [
  {
    num: "01",
    emoji: "🌌",
    title: "Not your average PDF resume.",
    body: "This is a fully interactive portfolio. Each planet orbiting the sun is a chapter — experience, projects, skills, education, certifications, and more. Everything is here.",
    flyTo: null,
    tip: null,
  },
  {
    num: "02",
    emoji: "🪐",
    title: "Click any planet.",
    body: "Hover a ring to reveal the planet name, then click to fly there and open the section. You can also click directly on the planet itself.",
    flyTo: "Mars",
    tip: "Flying to Experience →",
  },
  {
    num: "03",
    emoji: "◀▶",
    title: "Jump between sections.",
    body: "Use the ← → arrow buttons — or your keyboard arrows — to fly between planets without closing the panel. The Explore menu at the bottom gives direct access to any section.",
    flyTo: "Saturn",
    tip: "Flying to Skills →",
  },
  {
    num: "04",
    emoji: "🖱️",
    title: "The galaxy is yours.",
    body: "Drag to rotate. Scroll to zoom — all the way out and the Milky Way comes into view. No more instructions. Go explore.",
    flyTo: null,
    tip: null,
  },
];

const TutorialOverlay = ({ onComplete, flyForView, resetCamera }) => {
  const [step, setStep]     = useState(0);
  const [visible, setVisible] = useState(false);

  // Fade in after mount
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  // Camera movement on step change
  useEffect(() => {
    const { flyTo } = STEPS[step];
    if (flyTo) flyForView?.(flyTo);
  }, [step, flyForView]);

  const finish = useCallback(() => {
    setVisible(false);
    flyForView?.(null);           // cancel any in-flight animation
    setTimeout(() => {
      resetCamera?.();            // snap OrbitControls back to initial overview
    }, 50);
    setTimeout(onComplete, 400);
  }, [onComplete, flyForView, resetCamera]);

  const goTo = (i) => { if (i >= 0 && i < STEPS.length) setStep(i); };

  const current = STEPS[step];
  const isLast  = step === STEPS.length - 1;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        pointerEvents: "none",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.45s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        paddingBottom: 52,
      }}
    >
      {/* Skip */}
      <button
        onClick={finish}
        style={{
          position: "fixed",
          top: 22,
          right: 26,
          pointerEvents: "all",
          background: "rgba(255,179,217,0.08)",
          border: "1px solid rgba(255,179,217,0.22)",
          borderRadius: 40,
          color: "rgba(255,235,250,0.72)",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.78rem",
          letterSpacing: "0.07em",
          cursor: "pointer",
          transition: "all 0.2s",
          padding: "8px 18px",
        }}
        onMouseEnter={(e) => {
          e.target.style.background = "rgba(255,179,217,0.16)";
          e.target.style.color = "#FFF0F7";
          e.target.style.borderColor = "rgba(255,179,217,0.45)";
        }}
        onMouseLeave={(e) => {
          e.target.style.background = "rgba(255,179,217,0.08)";
          e.target.style.color = "rgba(255,235,250,0.72)";
          e.target.style.borderColor = "rgba(255,179,217,0.22)";
        }}
      >
        Skip Tutorial →
      </button>

      {/* Floating card */}
      <div
        key={step}
        style={{
          pointerEvents: "all",
          width: "min(500px, 92vw)",
          background: "rgba(6, 3, 18, 0.88)",
          border: "1px solid rgba(255,179,217,0.18)",
          borderRadius: 22,
          backdropFilter: "blur(22px)",
          padding: "28px 30px 24px",
          boxShadow:
            "0 0 0 1px rgba(199,125,255,0.07)," +
            "0 30px 70px rgba(0,0,0,0.65)," +
            "0 0 60px rgba(199,125,255,0.05)",
          animation: "tut-in 0.38s cubic-bezier(0.22, 1, 0.36, 1) both",
        }}
      >
        {/* Step counter + emoji row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <span
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: "0.62rem",
              letterSpacing: "0.18em",
              color: "rgba(255,179,217,0.5)",
            }}
          >
            {current.num} / 0{STEPS.length}
          </span>
          <span
            style={{
              marginLeft: "auto",
              fontSize: "1.6rem",
              filter: "drop-shadow(0 0 10px rgba(255,179,217,0.55))",
              animation: "tut-float 3s ease-in-out infinite",
            }}
          >
            {current.emoji}
          </span>
        </div>

        {/* Title */}
        <h3
          style={{
            fontFamily: "'Cinzel', serif",
            fontSize: "1.02rem",
            fontWeight: 600,
            color: "#FFF0F7",
            letterSpacing: "0.07em",
            margin: "0 0 10px",
          }}
        >
          {current.title}
        </h3>

        {/* Body */}
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.84rem",
            color: "rgba(255,235,250,0.62)",
            lineHeight: 1.68,
            margin: "0 0 20px",
          }}
        >
          {current.body}
        </p>

        {/* Tip line */}
        {current.tip && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 20,
              padding: "8px 14px",
              background: "rgba(199,125,255,0.06)",
              border: "1px solid rgba(199,125,255,0.12)",
              borderRadius: 10,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.73rem",
              color: "rgba(199,125,255,0.8)",
              letterSpacing: "0.03em",
              animation: "gentle-fade 0.5s ease 0.2s both",
            }}
          >
            <span style={{ fontSize: "0.85rem" }}>✦</span>
            {current.tip}
          </div>
        )}

        {/* Progress dots + buttons row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Dots */}
          <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                style={{
                  width: i === step ? 22 : 7,
                  height: 7,
                  borderRadius: 4,
                  border: "none",
                  background:
                    i === step
                      ? "linear-gradient(90deg,#FF6B9D,#C87FFF)"
                      : i < step
                      ? "rgba(255,179,217,0.4)"
                      : "rgba(255,179,217,0.15)",
                  cursor: "pointer",
                  padding: 0,
                  transition: "width 0.3s ease, background 0.3s ease",
                  boxShadow: i === step ? "0 0 8px rgba(255,107,157,0.5)" : "none",
                }}
              />
            ))}
          </div>

          <div style={{ flex: 1 }} />

          {/* Back */}
          {step > 0 && (
            <button
              onClick={() => goTo(step - 1)}
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.8rem",
                letterSpacing: "0.05em",
                cursor: "pointer",
                border: "1px solid rgba(255,179,217,0.14)",
                borderRadius: 40,
                padding: "9px 18px",
                background: "rgba(255,179,217,0.06)",
                color: "rgba(255,235,250,0.5)",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgba(255,179,217,0.12)";
                e.target.style.color = "rgba(255,235,250,0.8)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgba(255,179,217,0.06)";
                e.target.style.color = "rgba(255,235,250,0.5)";
              }}
            >
              ← Back
            </button>
          )}

          {/* Next / Enter */}
          <button
            onClick={isLast ? finish : () => goTo(step + 1)}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.82rem",
              letterSpacing: "0.06em",
              cursor: "pointer",
              border: "none",
              borderRadius: 40,
              padding: "10px 26px",
              background: "linear-gradient(135deg, rgba(255,107,157,0.75), rgba(180,100,255,0.70))",
              color: "#FFF0F7",
              boxShadow: "0 4px 20px rgba(255,107,157,0.28)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 6px 26px rgba(255,107,157,0.45)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 20px rgba(255,107,157,0.28)";
            }}
          >
            {isLast ? "Start Exploring →" : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
